import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@/store/hooks';
import { RootState } from '@/store/rootReducer';
import { LoadingStatus } from '@/enums/LoadingStatus.enum';
import { Division } from '@/types/Division';
import { setDivisions, setEntityStatus } from '@/store/slices/masterDataSlice';
import { masterDataIdbService } from '@/store/idbStorage/masterDataIdbService';
import type { MasterDataEntityKey } from '@/store/idbStorage/masterDataIdbService';
import { divisionsApi } from '@/api/divisionsApi';
import { captureApiFailure } from '@/utils/sentryApiError.util';

const ENTITY_KEY: MasterDataEntityKey = 'md_divisions';
const FALLBACK_DIVISIONS: Division[] = [{ id: '', name: 'All' }];

export const useDivisions = () => {
  const dispatch = useDispatch();
  const divisions = useAppSelector((state: RootState) => state.masterData.divisions);
  const status = useAppSelector(
    (state: RootState) => state.masterData.entityStatus['divisions'] ?? LoadingStatus.IDLE
  );

  useEffect(() => {
    if (divisions?.length || status === LoadingStatus.LOADING) {
      return;
    }

    const load = async () => {
      dispatch(setEntityStatus({ entity: 'divisions', status: LoadingStatus.LOADING }));
      try {
        const cached = await masterDataIdbService.getEntity<Division[]>(ENTITY_KEY);
        if (cached && cached.length) {
          dispatch(setDivisions(cached));
          dispatch(setEntityStatus({ entity: 'divisions', status: LoadingStatus.SUCCEEDED }));
          return;
        }
        const result = await divisionsApi.fetchDivisions();
        if (!result.ok) {
          captureApiFailure('Error fetching divisions', { status: result.status });
        }
        const apiDivisions = result.divisions || FALLBACK_DIVISIONS;
        dispatch(setDivisions(apiDivisions));
        await masterDataIdbService.setEntity(ENTITY_KEY, apiDivisions);
        dispatch(setEntityStatus({ entity: 'divisions', status: LoadingStatus.SUCCEEDED }));
      } catch (error) {
        captureApiFailure('Error fetching divisions', { error });
        dispatch(setEntityStatus({ entity: 'divisions', status: LoadingStatus.FAILED }));
      }
    };

    void load();
  }, [divisions?.length, status, dispatch]);

  return { divisions, status };
};
